import { useState } from "react";
import { Header } from "@/components/navigation/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Wrench, ShieldCheck, Download, Package, Zap } from "lucide-react";
import { DeploymentWizardDialog } from "@/components/deployment/DeploymentWizardDialog";

const Installer = () => {
  const [wizardOpen, setWizardOpen] = useState(false);
  
  const dellBlueprint = {
    id: 'dell-inference-stack',
    name: 'Dell Enterprise Inference Stack',
    description: 'On-premises AI inference with Gaudi accelerators',
    format: 'terraform' as const,
    icon: 'Package',
    content: '',
    filename: 'inference-setup.zip',
  };

  const handleDownloadAndStart = () => {
    // Trigger download
    const link = document.createElement('a');
    link.href = '/inference-setup.zip';
    link.download = 'inference-setup.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Open wizard
    setWizardOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-12">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto space-y-10">
            <div className="text-center space-y-3">
              <Badge variant="secondary" className="text-sm">
                Guided Deployment
              </Badge>
              <h1 className="text-4xl font-bold tracking-tight">Custom Installer Builder</h1>
              <p className="text-lg text-muted-foreground">
                Generate infrastructure-aware installers that bundle drivers, security policies, and optimized runtime settings
                for your Intel & Dell GenAI stack.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border-l-4 border-l-primary/60">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <Wrench className="h-5 w-5 text-primary" />
                    Guided Workflow
                  </CardTitle>
                  <CardDescription>
                    Answer a few environment questions and we will assemble a hardened installation package.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <p>• OS-specific drivers and firmware dependencies</p>
                  <p>• IaC templates for Kubernetes or bare metal rollouts</p>
                  <p>• Post-install validation scripts with health checks</p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-emerald-500/60">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <ShieldCheck className="h-5 w-5 text-emerald-500" />
                    Enterprise Guardrails
                  </CardTitle>
                  <CardDescription>
                    Bake in security baselines, RBAC policies, and networking rules directly into the installer payload.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <p>• Secrets handling for offline environments</p>
                  <p>• STIG-aligned OS hardening guidance</p>
                  <p>• Audit-ready configuration manifest</p>
                </CardContent>
              </Card>
            </div>

            <Card className="border-l-4 border-l-blue-500/60">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl flex items-center gap-2">
                      <Package className="h-6 w-6 text-blue-500" />
                      Self-Service Deployment
                    </CardTitle>
                    <CardDescription className="mt-2">
                      Download the inference setup bundle and follow our guided wizard to deploy Dell Enterprise Inference Stack on your infrastructure.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Zap className="h-4 w-4 text-yellow-500" />
                    <span>Guided 5-step workflow</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Download className="h-4 w-4 text-blue-500" />
                    <span>~250MB bundle</span>
                  </div>
                </div>
                
                <div className="pt-2">
                  <Button size="lg" className="w-full gap-2" onClick={handleDownloadAndStart}>
                    <Download className="h-5 w-5" />
                    Download & Start Guide
                  </Button>
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    Includes Terraform IaC, OS scripts, and driver packages
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <DeploymentWizardDialog
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        blueprint={dellBlueprint}
        target="local"
      />
    </div>
  );
};

export default Installer;
